import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimesheetForm = ({ entry, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    employee_name: "",
    machine_id: "",
    machine_name: "",
    workorder_number: "",
    date: new Date(),
    start_time: "",
    end_time: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [machines, setMachines] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);

  useEffect(() => {
    fetchEmployees();
    fetchMachines();
    fetchWorkOrders();
    if (entry) {
      setFormData({
        employee_name: entry.employee_name,
        machine_id: entry.machine_id,
        machine_name: entry.machine_name,
        workorder_number: entry.workorder_number,
        date: new Date(entry.date),
        start_time: entry.start_time,
        end_time: entry.end_time,
        notes: entry.notes || "",
      });
    }
  }, [entry]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees/names`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${API}/machines`);
      setMachines(response.data);
    } catch (error) {
      console.error("Error fetching machines:", error);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      // Fetch ONLY new work orders (not old ones)
      const response = await axios.get(`${API}/work-orders`);

      // Filter by status (available and in_progress)
      const filteredWorkOrders = response.data
        .filter(wo => wo.status === "available" || wo.status === "in_progress")
        .map(wo => ({
          work_order_number: wo.header.work_order_number,
          customer_name: wo.header.customer_name,
          item_name: wo.header.item_name
        }));

      setWorkOrders(filteredWorkOrders);
    } catch (error) {
      console.error("Error fetching work orders:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDateSelect = (date) => {
    setFormData({
      ...formData,
      date: date,
    });
  };

  const handleMachineSelect = (value) => {
    const machine = machines.find((m) => m.id === value);
    if (machine) {
      setFormData({
        ...formData,
        machine_id: machine.id,
        machine_name: machine.name,
      });
    }
  };

  const validateForm = () => {
    if (!formData.employee_name.trim()) {
      toast.error("Employee name is required");
      return false;
    }
    if (!formData.machine_id.trim()) {
      toast.error("Machine is required");
      return false;
    }
    if (!formData.workorder_number.trim()) {
      toast.error("Work order number is required");
      return false;
    }
    if (!formData.start_time) {
      toast.error("Start time is required");
      return false;
    }
    if (!formData.end_time) {
      toast.error("End time is required");
      return false;
    }
    if (formData.start_time >= formData.end_time) {
      toast.error("End time must be after start time");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      const submitData = {
        employee_name: formData.employee_name,
        machine_id: formData.machine_id,
        machine_name: formData.machine_name,
        workorder_number: formData.workorder_number,
        date: format(formData.date, "yyyy-MM-dd"),
        start_time: formData.start_time,
        end_time: formData.end_time,
        notes: formData.notes,
        status: formData.end_time ? "completed" : "ongoing",
      };

      if (entry) {
        await axios.put(`${API}/timesheet/${entry.id}`, submitData);
        toast.success("Entry updated successfully");
      } else {
        await axios.post(`${API}/timesheet`, submitData);
        toast.success("Entry added successfully");
      }

      onSuccess();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit entry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Employee Name */}
        <div className="space-y-2">
          <Label htmlFor="employee_name" className="text-gray-200">Employee Name *</Label>
          <Select
            value={formData.employee_name || "placeholder"}
            onValueChange={(value) => {
              if (value !== "placeholder") {
                setFormData({ ...formData, employee_name: value });
              }
            }}
          >
            <SelectTrigger
              data-testid="employee-select-trigger"
              className="w-full border-gray-600 bg-gray-800 text-white"
            >
              <SelectValue placeholder="Select employee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="placeholder" disabled>
                Select employee
              </SelectItem>
              {employees.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date */}
        <div className="space-y-2">
          <Label className="text-gray-200">Date *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                data-testid="date-picker-button"
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal border-gray-600 bg-gray-800 text-white hover:bg-gray-700",
                  !formData.date && "text-gray-400"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.date ? format(formData.date, "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={formData.date}
                onSelect={handleDateSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Work Order Number */}
        <div className="space-y-2">
          <Label htmlFor="workorder_number" className="text-gray-200">Work Order Number *</Label>
          <Select
            value={formData.workorder_number}
            onValueChange={(value) => setFormData({ ...formData, workorder_number: value })}
          >
            <SelectTrigger
              data-testid="workorder-select"
              className="border-gray-600 bg-gray-800 text-white"
            >
              <SelectValue placeholder="Select work order" />
            </SelectTrigger>
            <SelectContent>
              {workOrders.map((wo, index) => (
                <SelectItem key={wo.work_order_number || index} value={wo.work_order_number}>
                  <div className="flex flex-col">
                    <span className="font-semibold">{wo.work_order_number}</span>
                    {wo.customer_name && (
                      <span className="text-sm text-gray-500">
                        {wo.customer_name} {wo.item_name && `- ${wo.item_name}`}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Machine Name */}
        <div className="space-y-2">
          <Label htmlFor="machine_name" className="text-gray-200">Machine Name *</Label>
          <Select
            value={formData.machine_id || "placeholder"}
            onValueChange={(value) => {
              if (value !== "placeholder") {
                handleMachineSelect(value);
              }
            }}
          >
            <SelectTrigger
              data-testid="machine-select-trigger"
              className="w-full border-gray-600 bg-gray-800 text-white"
            >
              <SelectValue placeholder="Select a machine" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="placeholder" disabled>
                Select a machine
              </SelectItem>
              {machines.map((machine) => (
                <SelectItem
                  key={machine.id}
                  value={machine.id}
                  data-testid={`machine-option-${machine.id}`}
                >
                  {machine.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Start Time */}
        <div className="space-y-2">
          <Label htmlFor="start_time" className="text-gray-200">Start Time *</Label>
          <Input
            data-testid="start-time-input"
            id="start_time"
            name="start_time"
            type="time"
            value={formData.start_time}
            onChange={handleChange}
            className="border-gray-600 bg-gray-800 text-white"
          />
        </div>

        {/* End Time */}
        <div className="space-y-2">
          <Label htmlFor="end_time" className="text-gray-200">End Time *</Label>
          <Input
            data-testid="end-time-input"
            id="end_time"
            name="end_time"
            type="time"
            value={formData.end_time}
            onChange={handleChange}
            className="border-gray-600 bg-gray-800 text-white"
          />
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="notes" className="text-gray-200">Notes</Label>
        <Textarea
          data-testid="notes-input"
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          placeholder="Additional notes or task description..."
          className="border-gray-600 bg-gray-800 text-white min-h-[100px]"
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-3 justify-end">
        <Button
          data-testid="cancel-button"
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          className="px-6"
        >
          Cancel
        </Button>
        <Button
          data-testid="submit-button"
          type="submit"
          disabled={loading}
          className="btn-hover bg-blue-600 hover:bg-blue-700 text-white px-6"
        >
          {loading ? "Saving..." : entry ? "Update Entry" : "Add Entry"}
        </Button>
      </div>
    </form>
  );
};

export default TimesheetForm;